// ProfileLipSync — Avatar + voice combo, inline player
import { Mic, Pause, Play } from 'lucide-react'
import { useRef, useState } from 'react'

export interface LipSyncItem {
  id: number | string
  title: string
  video_url: string
  avatar_name?: string
  voice_name?: string
  thumbnail_url?: string
  created_at?: string
}

interface Props {
  items: LipSyncItem[]
  loading?: boolean
}

export default function ProfileLipSync({ items, loading }: Props) {
  const [playingId, setPlayingId] = useState<string | null>(null)
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({})

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary, #8b949e)', fontSize: 13 }}>Loading...</div>
  }

  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-secondary, #8b949e)' }}>
        <Mic size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
        <p style={{ fontSize: 14 }}>No LipSync creations yet</p>
      </div>
    )
  }

  const togglePlay = (id: string) => {
    const vid = videoRefs.current[id]
    if (!vid) return
    if (playingId === id) {
      vid.pause()
      setPlayingId(null)
    } else {
      // Diger videolari durdur
      Object.entries(videoRefs.current).forEach(([k, v]) => {
        if (k !== id && v) v.pause()
      })
      vid.play()
      setPlayingId(id)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {items.map(item => {
        const id = String(item.id)
        const isPlaying = playingId === id
        return (
          <div
            key={id}
            style={{
              display: 'flex', gap: 14, padding: 14, borderRadius: 14,
              border: '1px solid var(--border-default, #30363d)',
              background: 'rgba(255,255,255,0.02)',
              backdropFilter: 'blur(8px)',
              alignItems: 'center',
              transition: 'border-color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#667eea' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-default, #30363d)' }}
          >
            {/* Inline video player */}
            <div style={{ width: 120, height: 120, borderRadius: 12, overflow: 'hidden', flexShrink: 0, position: 'relative', background: '#000' }}>
              <video
                ref={el => { videoRefs.current[id] = el }}
                src={item.video_url}
                poster={item.thumbnail_url}
                loop
                playsInline
                onEnded={() => setPlayingId(null)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <button
                onClick={() => togglePlay(id)}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: isPlaying ? 'transparent' : 'rgba(0,0,0,0.35)',
                  border: 'none', cursor: 'pointer',
                }}
              >
                {!isPlaying && (
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'rgba(102,126,234,0.9)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Play size={14} color="#fff" fill="#fff" />
                  </div>
                )}
                {isPlaying && (
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1' }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0' }}
                  >
                    <Pause size={12} color="#fff" />
                  </div>
                )}
              </button>
            </div>

            {/* Info */}
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #e6edf3)', margin: '0 0 6px' }}>
                {item.title}
              </h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {item.avatar_name && (
                  <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'rgba(102,126,234,0.1)', color: '#667eea', fontWeight: 600 }}>
                    Avatar: {item.avatar_name}
                  </span>
                )}
                {item.voice_name && (
                  <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', fontWeight: 600 }}>
                    Voice: {item.voice_name}
                  </span>
                )}
              </div>
              {item.created_at && (
                <p style={{ fontSize: 11, color: 'var(--text-secondary, #8b949e)', margin: '8px 0 0' }}>
                  {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
